/**
 * Glitch levels — the "instability" trait.
 *
 * The brief is a one-second loop, twelve frames, stepped rather than interpolated.
 * The interesting decision is not how to break a frame but *how rarely*: a token
 * that glitches every frame is a screensaver, and the eye stops reading it as damage
 * within two loops. So a level is mostly a statement about scarcity — how many of
 * the twelve frames are hot, and how hard each hot frame hits.
 *
 * At least one frame is always clean, and the still image in `tokenURI`'s `image`
 * field is taken from a clean frame. That is a hard guarantee: a marketplace
 * thumbnail is the one view a collector cannot skip, and it should show the bird
 * whole. Everything else is allowed to tear.
 *
 * The five levels are an *ordering*, not five arbitrary tuples — `validateGlitch()`
 * asserts a strictly increasing composite intensity, so a level name always means
 * "more than the one below it". Without that a rarer level could easily be authored
 * calmer than a common one, and the trait would be noise in the metadata.
 *
 * Every field is an integer in renderer units, so a level can be handed to
 * `art/glitch.ts` unchanged and mirrored into Solidity as a `struct` of `uint16`s.
 */

import { GLITCH_FRAMES, GRID_COLS, GRID_ROWS } from "../constants.js";
import { MAX_EXTENT_CELLS } from "./archetypes.js";

/**
 * Cells of empty frame the creature budget leaves on each side.
 *
 * A displacement up to this can move the widest legal bird without losing a cell; one
 * cell further and the canvas clips. Exactly one level is allowed past it, and
 * `validateGlitch()` enforces that, because clipping is a signature rather than a
 * side effect.
 */
export const FRAME_MARGIN_CELLS = (GRID_COLS - MAX_EXTENT_CELLS) / 2;

export interface GlitchLevel {
  readonly name: string;

  /** How many of `GLITCH_FRAMES` carry displacement. Which ones is seeded. */
  readonly hotFrames: number;
  /** Horizontal bands that displace in a hot frame. */
  readonly sliceBands: number;
  /** Largest displacement of a band, in cells. Signed direction is seeded. */
  readonly maxSlice: number;
  /**
   * Chroma-split offset in cells. The grid is re-drawn twice more at +/- this
   * offset in two of the palette's own slots, which is what gives the tear its
   * colour fringe. 0 disables the ghosts and saves two `<use>` layers.
   */
  readonly chroma: number;
  /** Chance in 1/256 that a cell inside a hot band is replaced by another glyph. */
  readonly corrupt: number;
  /** Chance in 1/256 that a hot frame flashes: two colour slots swap for one frame. */
  readonly flicker: number;

  readonly weight: number;
}

export const GLITCH_LEVELS: readonly GlitchLevel[] = [
  {
    // One frame in twelve twitches, by one cell, in one band. Read at speed this is
    // a bird that blinks wrong — which is more unsettling than a bird that shreds,
    // and it is the most common level for that reason.
    name: "Stable",
    hotFrames: 1,
    sliceBands: 1,
    maxSlice: 1,
    chroma: 1,
    corrupt: 8,
    flicker: 0,
    weight: 26,
  },
  {
    // The baseline reading of the trait: two hot frames, so the loop has a rhythm
    // rather than a single event.
    name: "Flicker",
    hotFrames: 2,
    sliceBands: 2,
    maxSlice: 2,
    chroma: 1,
    corrupt: 20,
    flicker: 24,
    weight: 30,
  },
  {
    name: "Fracture",
    hotFrames: 3,
    sliceBands: 3,
    maxSlice: 3,
    chroma: 2,
    corrupt: 40,
    flicker: 40,
    weight: 24,
  },
  {
    // Escalates by *how much* of the grid moves rather than by how far. `maxSlice`
    // stays at Fracture's three cells because three is exactly the frame margin the
    // creature budget leaves — `MAX_EXTENT_CELLS` is 26 of 32 — and a fourth cell of
    // displacement would push a worst-case beak off the canvas. It was authored at 4
    // and the glitch test caught it against the margin. Clipping is Collapse's
    // signature and it should stay that way, so Cascade buys its intensity with five
    // bands instead of a wider throw.
    name: "Cascade",
    hotFrames: 4,
    sliceBands: 5,
    maxSlice: 3,
    chroma: 3,
    corrupt: 72,
    flicker: 64,
    weight: 12,
  },
  {
    // The only level that displaces further than the frame margin, so a Collapse
    // token genuinely loses cells off the canvas edge on its hot frames. That is
    // intended: `put()` drops off-grid writes rather than smearing them, so the
    // clipping reads as a signal dropout instead of a rendering bug. Rarest, and the
    // glitch test asserts it stays the only level that does this.
    name: "Collapse",
    hotFrames: 5,
    sliceBands: 7,
    maxSlice: 5,
    chroma: 4,
    corrupt: 120,
    flicker: 96,
    weight: 8,
  },
] as const;

export const GLITCH_WEIGHTS: readonly number[] = GLITCH_LEVELS.map((g) => g.weight);

/**
 * Composite intensity, used only to assert the levels escalate. The coefficients are
 * a rough visual weighting — a hot frame is the loudest thing, then how much of the
 * grid moves, then the surface effects — and nothing renders from this number.
 */
export function intensity(g: GlitchLevel): number {
  return g.hotFrames * 64 + g.sliceBands * g.maxSlice * 8 + g.chroma * 12 + g.corrupt + g.flicker;
}

/** Structural guard invoked by tests. */
export function validateGlitch(): void {
  const seen = new Set<string>();
  for (const g of GLITCH_LEVELS) {
    if (seen.has(g.name)) throw new Error(`duplicate glitch level: ${g.name}`);
    seen.add(g.name);
    if (g.weight <= 0) throw new Error(`${g.name}: weight must be positive`);

    // There is always a clean frame, because the still image is taken from one.
    if (g.hotFrames < 1) throw new Error(`${g.name}: a level with no hot frame is not a glitch`);
    if (g.hotFrames >= GLITCH_FRAMES) throw new Error(`${g.name}: leaves no clean frame for the still`);

    if (g.sliceBands < 1) throw new Error(`${g.name}: needs at least one band`);
    // Bands thinner than four rows stop reading as bands and become dither.
    if (g.sliceBands * 4 > GRID_ROWS) throw new Error(`${g.name}: ${g.sliceBands} bands are too thin to read`);

    if (g.maxSlice < 1) throw new Error(`${g.name}: needs a displacement of at least one cell`);
    // Past a quarter of the canvas a displacement stops reading as a shifted band
    // and reads as two unrelated images side by side.
    if (g.maxSlice * 4 > GRID_COLS) throw new Error(`${g.name}: displacement exceeds a quarter canvas`);

    if (g.chroma < 0 || g.chroma > 4) throw new Error(`${g.name}: chroma offset out of range`);
    if (g.corrupt < 0 || g.corrupt > 256) throw new Error(`${g.name}: corrupt must be in [0, 256]`);
    if (g.flicker < 0 || g.flicker > 256) throw new Error(`${g.name}: flicker must be in [0, 256]`);
  }

  for (let i = 1; i < GLITCH_LEVELS.length; i++) {
    const prev = GLITCH_LEVELS[i - 1] as GlitchLevel;
    const cur = GLITCH_LEVELS[i] as GlitchLevel;
    if (intensity(cur) <= intensity(prev)) {
      throw new Error(
        `${cur.name} (${intensity(cur)}) is not louder than ${prev.name} (${intensity(prev)});`
          + " the levels must be an ordering or the trait name means nothing",
      );
    }
    if (cur.hotFrames < prev.hotFrames) throw new Error(`${cur.name}: fewer hot frames than ${prev.name}`);
  }

  // Rarer must mean louder, in the same direction as the intensity ordering.
  for (let i = 1; i < GLITCH_LEVELS.length; i++) {
    const prev = GLITCH_LEVELS[i - 1] as GlitchLevel;
    const cur = GLITCH_LEVELS[i] as GlitchLevel;
    if (i >= 2 && cur.weight > prev.weight) {
      throw new Error(`${cur.name} is louder than ${prev.name} but no rarer`);
    }
  }

  // Only the rarest level may throw cells off the canvas. Cascade was authored at four
  // cells, one past the margin, which would have made dropout a 20% event instead of
  // the 8% signature it is meant to be.
  const clipping = GLITCH_LEVELS.filter((g) => g.maxSlice > FRAME_MARGIN_CELLS);
  if (clipping.length > 1) {
    throw new Error(
      `${clipping.map((g) => g.name).join(" and ")} all displace past the ${FRAME_MARGIN_CELLS}-cell`
        + " frame margin; only the rarest level may clip the canvas",
    );
  }
  const loudest = GLITCH_LEVELS[GLITCH_LEVELS.length - 1] as GlitchLevel;
  if (clipping.length === 1 && clipping[0] !== loudest) {
    throw new Error(`${(clipping[0] as GlitchLevel).name} clips the canvas but is not the rarest level`);
  }
}
